import { cva, type VariantProps } from 'class-variance-authority'
import { LucideProps } from 'lucide-react'
import {
  ButtonHTMLAttributes,
  cloneElement,
  FC,
  ReactElement,
  Ref
} from 'react'

import { cn } from '../utils/cn'

const BASE_BUTTON_CLASS_NAME =
  'flex gap-x-2 items-center rounded-lg font-medium transition-colors duration-300 cursor-pointer justify-center focus:outline-none focus:ring-2'

export const buttonVariants = cva(BASE_BUTTON_CLASS_NAME, {
  variants: {
    buttonType: {
      disabled: 'bg-gray-100 text-slate-500 cursor-not-allowed',
      white: 'bg-white hover:bg-slate-100 focus:ring-blue-500',
      gray: 'bg-gray-100 text-blue-600 hover:bg-gray-200 focus:ring-gray-200',
      grayDark: 'bg-gray-700 text-white hover:bg-gray-950 focus:ring-gray-200',
      primary: 'bg-blue-600 text-white hover:bg-blue-800 focus:ring-blue-300',
      red: 'bg-red-500 text-white hover:bg-red-700 focus:ring-red-200',
      secondary:
        'bg-blue-50 text-blue-600 hover:bg-blue-200 focus:ring-blue-500',
      transparent:
        'bg-none text-neutral-600 hover:text-black focus:ring-blue-200',
      green: 'bg-green-500 text-white hover:bg-green-700 focus:ring-green-200',
      yellow:
        'bg-yellow-500 text-yellow-950 hover:bg-yellow-700 focus:ring-yellow-200 hover:text-white',
      link: 'bg-none text-blue-800 hover:underline hover:underline-offset-2 hover:text-blue-900 focus:ring-blue-200'
    },
    buttonSize: {
      xs: 'px-2 h-8 text-xs',
      sm: 'px-3 h-9 text-sm',
      base: 'px-5 h-10 text-sm',
      l: 'px-5 h-12 text-base'
    },
    buttonShape: {
      square: 'p-0'
    },
    buttonVariant: {
      outline: 'border bg-transparent'
    }
  },
  compoundVariants: [
    {
      buttonSize: 'xs',
      buttonShape: 'square',
      className: '!size-8'
    },
    {
      buttonSize: 'sm',
      buttonShape: 'square',
      className: '!size-9'
    },
    {
      buttonSize: 'base',
      buttonShape: 'square',
      className: '!size-10'
    },
    {
      buttonSize: 'l',
      buttonShape: 'square',
      className: '!size-12'
    },
    {
      buttonVariant: 'outline',
      buttonType: 'primary',
      className:
        'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
    },
    {
      buttonVariant: 'outline',
      buttonType: 'red',
      className: 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white'
    },
    {
      buttonVariant: 'outline',
      buttonType: 'green',
      className:
        'border-green-600 text-green-600 hover:bg-green-600 hover:text-white'
    },
    {
      buttonVariant: 'outline',
      buttonType: 'grayDark',
      className:
        'border-gray-700 text-gray-700 hover:bg-gray-700 hover:text-white'
    }
  ],
  defaultVariants: {
    buttonSize: 'base',
    buttonType: 'primary'
  }
})

export const buttonIconVariants = cva('shrink-0', {
  variants: {
    buttonSize: {
      xs: 'size-4',
      sm: 'size-5',
      base: 'size-5',
      l: 'size-6'
    }
  },
  defaultVariants: {
    buttonSize: 'base'
  }
})

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  leftIconSlot?: ReactElement<LucideProps>
  rightIconSlot?: ReactElement<LucideProps>
  fullWidth?: boolean
  pill?: boolean
  border?: boolean
  ref?: Ref<HTMLButtonElement>
}

export const Button: FC<ButtonProps> = ({
  children,
  className,
  buttonType,
  buttonSize,
  buttonShape,
  buttonVariant,
  disabled,
  leftIconSlot,
  rightIconSlot,
  fullWidth,
  pill,
  border,
  ref,
  ...props
}) => {
  const buttonClassName = cn(
    buttonVariants({
      buttonSize,
      buttonShape,
      buttonVariant,
      buttonType: disabled ? 'disabled' : buttonType
    }),
    {
      'w-full': fullWidth,
      'rounded-full': pill,
      'border border-solid border-yd-blue': border
    },
    className
  )

  const leftIcon = leftIconSlot
    ? cloneElement(leftIconSlot, {
        'aria-hidden': 'true',
        className: cn(
          buttonIconVariants({ buttonSize }),
          leftIconSlot.props.className
        )
      })
    : null

  const rightIcon = rightIconSlot
    ? cloneElement(rightIconSlot, {
        'aria-hidden': 'true',
        className: cn(
          buttonIconVariants({ buttonSize }),
          rightIconSlot.props.className
        )
      })
    : null

  return (
    <button
      {...props}
      className={buttonClassName}
      disabled={disabled}
      tabIndex={0}
      ref={ref}>
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  )
}
